import React, { useState, useEffect } from "react";
import {
  Navbar,
  Container,
  Button,
  Nav,
  NavItem,
  NavLink,
  Collapse,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Media,
} from "reactstrap";
import {
  NavLink as NavLinkRRD,
  Link,
  useLocation,
  useNavigate,
  matchPath,
} from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";

// ✅ ใช้โลโก้จาก assets/logo.png
import logoImg from "../../assets/logo.png";

const LG_MIN = 992;

const MobileSidebarNav = ({ routes = [], logo }) => {
  /* ================= screen width ================= */
  const [isMobileWidth, setIsMobileWidth] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth < LG_MIN
  );

  useEffect(() => {
    const onResize = () => setIsMobileWidth(window.innerWidth < LG_MIN);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ================= hooks ================= */
  const [isOpen, setIsOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const username = user?.username;
  const avatar = user?.avatar || "https://i.pravatar.cc/100?img=12";
  const role = user?.role?.toLowerCase() === "admin" ? "admin" : "user";

  if (!isMobileWidth) return null;

  /* ================= helpers ================= */
  const isHidden = (v) => v === true || v === "true" || v === 1 || v === "1";
  const canSee = (item = {}) =>
    !isHidden(item.hidden) &&
    (!Array.isArray(item.roles) ||
      item.roles.length === 0 ||
      item.roles.includes(role));
  const visible = (arr = []) => arr.filter(canSee);

  // ✅ FIX: active แบบ exact
  const isActiveExact = (path) =>
    !!matchPath({ path, end: true }, location.pathname);

  const toggleGroup = (key) => setOpenGroups((s) => ({ ...s, [key]: !s[key] }));

  const handleLogout = () => {
    try {
      logout?.();
      localStorage.clear();
      sessionStorage.clear();
    } finally {
      navigate("/", { replace: true });
    }
  };

  const renderIconSmall = (icon) => {
    if (!icon) return null;
    const common = { style: { width: 20, height: 20, minWidth: 20 } };
    if (typeof icon === "string") return <i className={icon} {...common} />;
    if (React.isValidElement(icon))
      return React.cloneElement(icon, { className: "nav-icon", ...common });
    if (typeof icon === "function") {
      const C = icon;
      return <C className="nav-icon" {...common} />;
    }
    return null;
  };

  /* ================= menu builder ================= */
  const createLinks = (allRoutes) =>
    visible(allRoutes).map((prop, key) => {
      /* ===== GROUP ===== */
      if (Array.isArray(prop.children) && prop.children.length) {
        const children = visible(prop.children);
        if (!children.length) return null;

        const anyChildActive = children.some((c) => isActiveExact(c.path));
        const open = openGroups[key] ?? anyChildActive;

        return (
          <div key={`grp-${key}`} className="mb-1">
            <button
              type="button"
              className={`mobile-group-btn ${anyChildActive ? "active" : ""}`}
              onClick={() => toggleGroup(key)}
            >
              <span className="d-flex align-items-center">
                {renderIconSmall(prop.icon)}
                <span className="ml-2">{prop.name}</span>
              </span>
              <i className={`ni ${open ? "ni-bold-down" : "ni-bold-right"}`} />
            </button>

            <Collapse isOpen={open}>
              <Nav className="nav-sm flex-column pl-3">
                {children.map((child, idx) => (
                  <NavItem key={`child-${key}-${idx}`}>
                    <NavLink
                      to={child.path}
                      tag={NavLinkRRD}
                      onClick={() => setIsOpen(false)}
                      className={`mobile-sub-link ${
                        isActiveExact(child.path) ? "active" : ""
                      }`}
                    >
                      {renderIconSmall(child.icon)}
                      <span className="ml-2">{child.name}</span>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </Collapse>
          </div>
        );
      }

      /* ===== LEAF ===== */
      return (
        <NavItem key={key}>
          <NavLink
            to={prop.path}
            tag={NavLinkRRD}
            onClick={() => setIsOpen(false)}
            className={`mobile-root-link d-flex align-items-center ${
              isActiveExact(prop.path) ? "active" : ""
            }`}
          >
            {renderIconSmall(prop.icon)}
            <span className="ml-2">{prop.name}</span>
          </NavLink>
        </NavItem>
      );
    });

  /* ================= render ================= */
  return (
    <>
      {/* ===== Top bar ===== */}
      <Navbar
        light
        className="shadow-sm d-lg-none mobile-topbar"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1030,
          background: "linear-gradient(10deg,#60efff 0%,#0061ff 100%)",
        }}
      >
        <Container fluid className="d-flex justify-content-between">
          <Button color="link" className="p-0" onClick={() => setIsOpen(true)}>
            <i className="ni ni-bullet-list-67" style={{ color: "#fff" }} />
          </Button>

          <UncontrolledDropdown nav inNavbar>
            <DropdownToggle nav className="p-0">
              <Media className="align-items-center">
                <span className="avatar avatar-sm rounded-circle">
                  <img alt={username} src={avatar} />
                </span>
              </Media>
            </DropdownToggle>
            <DropdownMenu right>
              {/* <DropdownItem tag={Link} to="/user-profile">
                ข้อมูลส่วนตัว
              </DropdownItem>
              <DropdownItem divider /> */}
              <DropdownItem onClick={handleLogout}>Log Out</DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </Container>
      </Navbar>

      {/* ===== Drawer ===== */}
      <div className={`mobile-drawer ${isOpen ? "open" : ""}`}>
        <div className="mobile-drawer-inner">
          <div className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom">
            {/* ✅ บังคับใช้โลโก้จาก assets/logo.png */}
            <Link to="/index" onClick={() => setIsOpen(false)}>
              <img
                src={logoImg}
                alt="Logo"
                style={{
                  height: 26,
                  width: "auto",
                  display: "block",
                }}
              />
            </Link>

            <Button close onClick={() => setIsOpen(false)} />
          </div>

          <Nav navbar className="p-2">
            <Collapse isOpen>{createLinks(routes)}</Collapse>
          </Nav>
        </div>

        <div
          className="mobile-drawer-backdrop"
          onClick={() => setIsOpen(false)}
        />
      </div>

      {/* Inline CSS */}
      <style>{`
        .mobile-root-link,
        .mobile-sub-link,
        .mobile-group-btn { border-radius: 10px; }

        .mobile-root-link { font-size: 0.95rem; padding: 0.45rem 0.65rem; }
        .mobile-sub-link { font-size: 0.95rem; padding: 0.45rem 0.65rem; }
        .mobile-group-btn {
          font-size: 0.95rem; width: 100%; display: flex; align-items: center;
          justify-content: space-between; padding: 0.5rem 0.65rem;
          background: transparent; border: none; color: #1171ef; font-weight: 400;
          cursor: pointer;
        }
        .mobile-root-link.active, .mobile-sub-link.active {
          background: rgba(17,113,239,0.12); color: #1171ef !important; font-weight: 400;
        }
        .mobile-root-link:hover, .mobile-sub-link:hover, .mobile-group-btn:hover {
          background: rgba(17,113,239,0.08); text-decoration: none;
        }
        .mobile-group-btn .ni { font-size: 0.9rem; opacity: .85; }
      `}</style>
    </>
  );
};

export default MobileSidebarNav;
